import { useRedisWritesBlocked } from '@/apis/call';
import { updateList } from '@/apis/lists';
import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useSimpleCompleteList = (listId: number) => {
  const { getAccessTokenSilently } = useAuth0();
  const { enqueueSuccessNotification, enqueueErrorNotification } = useSaladBar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return updateList(token, listId, { isComplete: true });
    },
    onSuccess: () => {
      enqueueSuccessNotification('List has been completed');
      queryClient.invalidateQueries({ queryKey: ['list', listId] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
    onError: () => enqueueErrorNotification('List could not be completed'),
  });
};
interface Props {
  listId: number;
  listName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const SimpleCompleteListDialog = ({
  listId,
  listName,
  isOpen,
  onClose: handleClose,
}: Props) => {
  const { mutateAsync: completeList } = useSimpleCompleteList(listId);
  const writesBlocked = useRedisWritesBlocked();
  const handleCompleteListDialogSave = async () => {
    if (writesBlocked) return;
    try {
      await completeList();
      handleClose();
    } catch {
      // The mutation onError already toasts. Leave the dialog open.
    }
  };

  return (
    <AgDialog
      isOpen={isOpen}
      onClose={handleClose}
      dialogTitle={'Complete this list?'}
      primaryButton={{
        text: 'Complete',
        onClick: handleCompleteListDialogSave,
        disabled: writesBlocked,
      }}
      data-testid="SimpleCompleteList"
    >
      <Typography sx={{
        marginBottom: 1
      }}>
        Are you sure you want to complete the list <strong>{listName}?</strong>
      </Typography>
      <Typography>
        <em>Note: this will complete the list for all assigned users.</em>
      </Typography>
    </AgDialog>
  );
};
